import React, { useCallback } from "react";
import { connect } from "react-redux";
import Grid from "@material-ui/core/Grid";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import get from "lodash/get";
import { format, isValid, addYears, addDays } from "date-fns";
import { getSignatories } from "../../../../store/selectors/appConfig";
import {
  AutoSaveField as Field,
  SelectAutocomplete,
  EmiratesID,
  InlineRadioGroup,
  DatePicker
} from "../../../../components/Form";
import { SubmitButton } from "./../SubmitButton/SubmitButton";
import { EMIRATES_ID_REGEX } from "../../../../utils/validation";
import {
  getRequiredMessage,
  getInvalidMessage,
  getRequiredNotTextInputMessage
} from "../../../../utils/getValidationMessage";
import { yesNoOptions } from "../../../../constants/options";
import { DATE_FORMAT, UAE, RESIDENT_VALUE } from "../../../../constants";

const createCountryOfResidenceSchema = isSignatory =>
  Yup.object().shape({
    residenceStatus: Yup.string().required(getRequiredMessage("Resident Status")),
    residenceCountry: Yup.string().required(getRequiredMessage("Country of residence")),
    eidNumber: Yup.string().when("residenceCountry", {
      is: value => value === UAE,
      then: Yup.string()
        .required(getRequiredMessage("Emirates ID"))
        .transform(value => value.replace(/-/g, ""))
        .matches(EMIRATES_ID_REGEX, getInvalidMessage("Emirates ID"))
    }),
    emiratesIdExpiryDate: Yup.date().when("residenceCountry", {
      is: value => value === UAE,
      then: Yup.date()
        .nullable()
        .required(getRequiredMessage("Emirates ID Expiry Date"))
        .min(addDays(new Date(), 10), "Emirates ID Expiry Date must be in the future")
        .typeError("Invalid Emirates ID Expiry Date")
    }),
    isUSrelation:
      isSignatory &&
      Yup.boolean()
        .nullable()
        .required(getRequiredNotTextInputMessage("I have US relation"))
  });

const changeDateProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(value, DATE_FORMAT) };

const CountryOfResidenceStep = ({
  index,
  isSignatory,
  handleContinue,
  createFormChangeHandler
}) => {
  const kycDetailsPath = `prospect.signatoryInfo[${index}].kycDetails`;
  const pathToEidNumber = `${kycDetailsPath}.emirateIdDetails.eidNumber`;
  const pathToEidExpiryDate = `${kycDetailsPath}.emirateIdDetails.eidExpiryDt`;
  const pathToIsUAEResident = `${kycDetailsPath}.isUAEResident`;
  const pathToCountryofResidence = `${kycDetailsPath}.residenceCountry`;

  const selectUsRelation = ({ values, setFieldValue }) => event => {
    const value = JSON.parse(event.target.value);
    setFieldValue("isUSrelation", value);
  };
  /*istanbul ignore next*/
  const changeResidenceProspectHandler = (prospect, value) => {
    let result = {
      ...prospect,
      [pathToIsUAEResident]: value === UAE
    };
    if (value !== UAE) {
      result[pathToEidNumber] = "";
      result[pathToEidExpiryDate] = "";
    }
    return result;
  };

  const changeResidenceStatusProspectHandler = (prospect, value) => {
    let result = { ...prospect };
    result[pathToIsUAEResident] = value === RESIDENT_VALUE;
    if (value === RESIDENT_VALUE) {
      result[pathToCountryofResidence] = UAE;
    } else {
      Object.assign(result, {
        [pathToEidNumber]: "",
        [pathToEidExpiryDate]: ""
      });
    }
    return result;
  };

  const onChangeResidenceStatusHandler = useCallback((value, setFieldValue) => {
    setFieldValue("residenceStatus", value);
    if (value !== RESIDENT_VALUE) {
      setFieldValue("residenceCountry", "");
      setFieldValue("eidNumber", "");
      setFieldValue("emiratesIdExpiryDate", "");
    } else {
      setFieldValue("residenceCountry", UAE);
    }
  }, []);

  return (
    <Formik
      initialValues={{
        residenceStatus: RESIDENT_VALUE,
        residenceCountry: UAE,
        eidNumber: "",
        emiratesIdExpiryDate: "",
        isUSrelation: "",
        isSignatory
      }}
      onSubmit={handleContinue}
      validationSchema={createCountryOfResidenceSchema(isSignatory)}
      validateOnChange={false}
    >
      {createFormChangeHandler(({ values, setFieldValue }) => {
        const preferredMailingAddress = selectUsRelation({ values, setFieldValue });
        return (
          <Form>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <Field
                  name="residenceStatus"
                  path={`${kycDetailsPath}.residenceStatus`}
                  label="Resident Status"
                  component={SelectAutocomplete}
                  datalistId="residentStatus"
                  defaultValue="RESIDENT_VALUE"
                  shrink
                  tabIndex="0"
                  changeProspect={changeResidenceStatusProspectHandler}
                  onChange={value => onChangeResidenceStatusHandler(value, setFieldValue)}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Field
                  name="residenceCountry"
                  path={pathToCountryofResidence}
                  label="Country of residence"
                  component={SelectAutocomplete}
                  disabled={values.residenceStatus === RESIDENT_VALUE}
                  datalistId="countryOfIncorporation"
                  shrink
                  tabIndex="0"
                  changeProspect={changeResidenceProspectHandler}
                  onChange={value => {
                    setFieldValue("residenceCountry", value);
                    if (value !== UAE) {
                      setFieldValue("eidNumber", "");
                      setFieldValue("emiratesIdExpiryDate", "");
                    }
                  }}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Field
                  name="eidNumber"
                  path={pathToEidNumber}
                  label="Emirates ID"
                  placeholder="784-1950-1234567-8"
                  disabled={
                    values.residenceCountry !== UAE && values.residenceStatus !== RESIDENT_VALUE
                  }
                  component={EmiratesID}
                  changeProspect={(prospect, value) => ({
                    ...prospect,
                    [pathToEidNumber]: value.replace(/-/g, "")
                  })}
                  contextualHelpText={
                    <>
                      If Emirates ID contains hyphen (-), spaces or any other special character
                      please enter only alphabets and numbers
                      <br />
                      Example
                      <br />
                      784-1950-1234567-8 to be entered as 784195012345678
                    </>
                  }
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <Field
                  name="emiratesIdExpiryDate"
                  path={pathToEidExpiryDate}
                  label="Emirates ID Expiry Date"
                  disabled={
                    values.residenceCountry !== UAE && values.residenceStatus !== RESIDENT_VALUE
                  }
                  disableFuture={false}
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  minDate={addDays(new Date(), 10)}
                  maxDate={addYears(new Date(), 20)}
                />
              </Grid>
            </Grid>
            {isSignatory && (
              <Grid container>
                <Field
                  name="isUSrelation"
                  component={InlineRadioGroup}
                  path={`prospect.signatoryInfo[${index}].isUSrelation`}
                  options={yesNoOptions}
                  label="I have US relation"
                  onChange={preferredMailingAddress}
                  contextualHelpProps={{ isDisableHoverListener: false, placement: "bottom-end" }}
                  contextualHelpText="You have connections with US if - You are a US national / Resident of the USA / Holder of a US Green Card / Tax Resident of US, and that I have stated U.S as one of the countries in the previous section. (If selected please complete W9 form)"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
            )}
            <SubmitButton />
          </Form>
        );
      })}
    </Formik>
  );
};

const mapStateToProps = (state, { index }) => {
  const isSignatory = get(getSignatories(state)[index], "kycDetails.isSignatory", false);
  return {
    isSignatory
  };
};

export const CountryOfResidence = connect(mapStateToProps)(CountryOfResidenceStep);
